import { findLowestPrice } from '@/components/product-price/utils';
import type { OmnibusPriceAttributes } from '@/types/scapi';

type PriceSource = Parameters<typeof findLowestPrice>[0];

type OmnibusSource = {
    [K in keyof OmnibusPriceAttributes as `c_${K}`]?: unknown;
};

type ProductTypeSource = {
    c_productType?: unknown;
    representedProduct?: { c_productType?: unknown } | null;
};

/**
 * `c_productType` values in scope of the price indication (EATWS-472: grocery only; events,
 * courses and Eatinerary are deferred, gift cards cannot be discounted). Untyped products are
 * treated as grocery, matching the SCAPI hook.
 */
const ELIGIBLE_PRODUCT_TYPES: ReadonlySet<unknown> = new Set(['physical']);

/** Omnibus directive data for the product whose price is displayed. */
export type OmnibusPriceIndication = {
    /** Lowest price of the 30 days before the current price period; null when history is unavailable. */
    priorBestPrice: number | null;
    /** First day (`yyyy-MM-dd`) of the active promotion / sale price validity. */
    validFrom: string | null;
    /** Last day (`yyyy-MM-dd`) of the active promotion / sale price validity. */
    validTo: string | null;
};

/** `OmnibusPriceIndication` enriched with the display strings computed by `useProductPriceWeightDisplay`. */
export type OmnibusPriceDisplay = OmnibusPriceIndication & {
    /** Formatted previous total price (struck through), null when not on a reduction. */
    priorFormatted: string | null;
    /** Formatted previous price per unit of measure (`€/kg`), null when not applicable. */
    priorPerUnitLine: string | null;
};

/** Whether the product kind is in scope of the price indication. */
export function isOmnibusEligibleProduct(product: ProductTypeSource | null | undefined): boolean {
    if (!product) return false;
    const productType = product.c_productType ?? product.representedProduct?.c_productType;
    return productType == null || productType === '' || ELIGIBLE_PRODUCT_TYPES.has(productType);
}

function readIndication(source: OmnibusSource | undefined): OmnibusPriceIndication | null {
    if (!source) return null;
    const priorBestPrice =
        typeof source.c_priorBestPrice === 'number' && source.c_priorBestPrice > 0 ? source.c_priorBestPrice : null;
    const validFrom =
        typeof source.c_priceValidFrom === 'string' && source.c_priceValidFrom ? source.c_priceValidFrom : null;
    const validTo = typeof source.c_priceValidTo === 'string' && source.c_priceValidTo ? source.c_priceValidTo : null;

    if (priorBestPrice === null && !validFrom && !validTo) return null;
    return { priorBestPrice, validFrom, validTo };
}

/**
 * Reads the `c_priorBestPrice` / `c_priceValidFrom` / `c_priceValidTo` attributes injected by the
 * SCAPI hooks for the product whose price is shown: the lowest-priced variant for master products
 * (the same one `getPriceData` displays), otherwise the product, search hit or basket line itself.
 * Falls back to the hit-level attributes (represented product) when the variant carries none.
 * Returns null for product kinds out of scope (events, courses, gift cards).
 */
export function getOmnibusPriceIndication(product: PriceSource | undefined | null): OmnibusPriceIndication | null {
    if (!product || !isOmnibusEligibleProduct(product as ProductTypeSource)) return null;
    const priceContext = findLowestPrice(product)?.data;
    return readIndication(priceContext as OmnibusSource | undefined) ?? readIndication(product as OmnibusSource);
}
